import { css } from '@emotion/react';
import {
  CallEvent,
  EventType,
  ScheduleType,
} from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records-ext/call-path/v1/api.pb';
import dayjs from 'dayjs';
import { startCase } from 'lodash-es';
import { Trans, i18next } from '@frontend/i18n';
import { IconName } from '@frontend/icons';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { theme } from '@frontend/theme';
import { Accordion, Chip, Text, Truncated } from '@frontend/design-system';
import { Dot } from '../components/dot.component';
import { EventTypeItemBox } from '../components/event-type-item-box';
import { DialOptionBadge, FallbackChip } from '../components/fallback.component';

export const renderCallEventTypeItem = (
  callDetail: CallEvent,
  { callerName, userName }: { callerName?: string; userName?: string }
) => {
  switch (callDetail.eventType) {
    case EventType.EVENT_TYPE_CONDITION: {
      if ('conditionEvent' in callDetail) {
        const { conditionEvent } = callDetail;
        const name = conditionEvent.schedule?.scheduleName ?? conditionEvent.conditionName;
        const type = conditionEvent.schedule?.type;
        const isOpen = type === ScheduleType.OPEN;
        const isClosed = type === ScheduleType.CLOSED;
        const chipColor = isOpen ? 'success' : isClosed ? 'critical' : 'indigo';

        return (
          <div className='event-type-box' css={{ textAlign: 'center', position: 'relative' }}>
            <Chip isResponsive variant={chipColor}>
              {startCase(name)}
            </Chip>
          </div>
        );
      }
      break;
    }

    case EventType.EVENT_TYPE_DEVICE: {
      if ('deviceEvent' in callDetail) {
        const {
          createdAt,
          deviceEvent: { user, deviceName, deviceId },
        } = callDetail;
        const startTime = createdAt ? dayjs(createdAt).format('hh:mm A') : '';
        const subtitle = user?.firstName ? `${user.firstName} ${user.lastName} (${deviceName})` : `${deviceName}`;
        return (
          <EventTypeItemBox
            key={deviceId}
            iconName='cell-phone'
            title={i18next.t('Device', { ns: 'calls' })}
            subtitle={subtitle}
            endAdornment={startTime}
          />
        );
      }
      break;
    }

    case EventType.EVENT_TYPE_CALL_ENDED: {
      if ('callEndedEvent' in callDetail) {
        const { callEndedEvent } = callDetail;
        const { callEndedAt, callEndedByCaller } = callEndedEvent;
        const endedAt = callEndedAt ? dayjs(callEndedAt).format('hh:mm A') : '';
        const callEndedByName = callEndedByCaller ? callerName : userName;

        const subTitle = !!callEndedByName
          ? i18next.t('{{ name }} hangs up', { ns: 'calls', name: callEndedByName })
          : '';

        return (
          <EventTypeItemBox
            key={callEndedAt}
            iconName='phone-hangup'
            title={i18next.t('Call Ended', { ns: 'calls' })}
            endAdornment={endedAt}
            subtitle={subTitle}
          />
        );
      }
      break;
    }

    case EventType.EVENT_TYPE_PLAY_MEDIA: {
      if ('playMediaEvent' in callDetail) {
        const { createdAt } = callDetail;
        const { mediaId, mediaName } = callDetail.playMediaEvent;
        const createdAtTime = createdAt ? dayjs(createdAt).format('hh:mm A') : '';
        const regex = /phone config/i;
        const containsPhoneConfig = mediaName && regex.test(mediaName);
        const subtitle = containsPhoneConfig ? i18next.t('Voicemail Greeting', { ns: 'calls' }) : mediaName;

        return (
          <EventTypeItemBox
            key={mediaId}
            iconName='speaker'
            title={i18next.t('Play Message', { ns: 'calls' })}
            subtitle={subtitle}
            endAdornment={createdAtTime}
          />
        );
      }
      break;
    }

    case EventType.EVENT_TYPE_FORWARDED: {
      if ('forwardedEvent' in callDetail) {
        const {
          forwardedEvent: { forwardedTo, forwardedNumberName },
          createdAt,
        } = callDetail;
        const startTime = createdAt ? dayjs(createdAt).format('hh:mm A') : '';
        const subtitle = forwardedNumberName
          ? `${forwardedNumberName} ${formatPhoneNumber(forwardedTo)}`
          : `${formatPhoneNumber(forwardedTo)}`;

        return (
          <EventTypeItemBox
            key={forwardedTo}
            iconName='send-forward'
            title={i18next.t('Forwarding Number', { ns: 'calls' })}
            subtitle={subtitle}
            endAdornment={startTime}
          />
        );
      }
      break;
    }

    case EventType.EVENT_TYPE_VOICEMAIL_PROMPT: {
      if ('voicemailPromptEvent' in callDetail) {
        const { createdAt } = callDetail;
        const { voicemailBoxId, voicemailBoxName, transcription } = callDetail.voicemailPromptEvent;
        const createdAtTime = createdAt ? dayjs(createdAt).format('hh:mm A') : '';

        if (transcription) {
          return (
            <AccordionEventTypeItem
              event={callDetail}
              eventKey={`voicemail-prompt-${voicemailBoxId}`}
              iconName='voicemail'
              title={i18next.t('Voicemail', { ns: 'calls' })}
              subtitle={voicemailBoxName}
              callerName={callerName}
            />
          );
        }

        return (
          <EventTypeItemBox
            key={voicemailBoxId}
            iconName='voicemail'
            title={i18next.t('Voicemail', { ns: 'calls' })}
            subtitle={voicemailBoxName}
            endAdornment={createdAtTime}
          />
        );
      }
      break;
    }

    case EventType.EVENT_TYPE_CALL_QUEUE: {
      if ('callQueueEvent' in callDetail) {
        const {
          createdAt,
          callQueueEvent: { callQueueName, callQueueId },
        } = callDetail;
        const startTime = createdAt ? dayjs(createdAt).format('hh:mm A') : '';

        return (
          <EventTypeItemBox
            key={callQueueId}
            iconName='call-queue'
            title={i18next.t('Call Queue', { ns: 'calls' })}
            subtitle={callQueueName}
            endAdornment={startTime}
          />
        );
      }
      break;
    }

    case EventType.EVENT_TYPE_CALL_GROUP: {
      if ('callGroupEvent' in callDetail) {
        const {
          createdAt,
          callGroupEvent: { callGroupName, callGroupId },
        } = callDetail;
        const startTime = createdAt ? dayjs(createdAt).format('hh:mm A') : '';

        return (
          <EventTypeItemBox
            key={callGroupId}
            iconName='call-group'
            title={i18next.t('Call Group', { ns: 'calls' })}
            subtitle={callGroupName}
            endAdornment={startTime}
          />
        );
      }
      break;
    }

    case EventType.EVENT_TYPE_IVR: {
      if ('ivrEvent' in callDetail) {
        const { exitedAt, ivrMenuName, ivrMenuId, dtmfEvents } = callDetail.ivrEvent;
        const exitedAtTime = exitedAt ? dayjs(exitedAt).format('hh:mm A') : '';
        const hasFallback = callDetail?.ivrEvent.dialOption === 'FALLBACK';
        const hasDTMFEvents = dtmfEvents && dtmfEvents.length > 0;

        return (
          <>
            {hasDTMFEvents ? (
              <AccordionEventTypeItem
                event={callDetail}
                eventKey={`ivr-${callDetail.ivrEvent.ivrMenuId}`}
                iconName='phone-tree'
                title={i18next.t('Phone Tree', { ns: 'calls' })}
                subtitle={ivrMenuName}
                callerName={callerName}
              />
            ) : (
              <EventTypeItemBox
                key={ivrMenuId}
                iconName='phone-tree'
                title={i18next.t('Phone Tree', { ns: 'calls' })}
                subtitle={ivrMenuName}
                endAdornment={exitedAtTime}
                secondaryText={i18next.t('No option dialed', { ns: 'calls' })}
              />
            )}
            <div className='event-type-box' css={{ textAlign: 'center', position: 'relative' }}>
              {hasFallback && <FallbackChip />}
              {!!callDetail.ivrEvent.dialOption && !hasFallback && (
                <DialOptionBadge displayValue={callDetail.ivrEvent.dialOption} />
              )}
            </div>
          </>
        );
      }
      break;
    }

    default: {
      return null;
    }
  }

  return null;
};

interface AccordionEventTypeItemProps<T extends CallEvent = CallEvent> {
  event: T;
  iconName: IconName;
  eventKey: string;
  title: string;
  subtitle?: string;
  callerName?: string;
}

const AccordionEventTypeItem = <T extends CallEvent>({
  event,
  eventKey,
  iconName,
  title,
  subtitle,
  callerName,
}: AccordionEventTypeItemProps<T>) => {
  const renderChild = getEventBody(event, callerName);

  return (
    <Accordion
      distance={0}
      key={eventKey}
      className='event-type-box call-path-mapping-accordion'
      css={{ position: 'relative' }}
      padding={0}
    >
      <Accordion.Item value={eventKey}>
        <Accordion.Header>
          <EventTypeItemBox
            key={eventKey}
            iconName={iconName}
            title={title}
            subtitle={subtitle}
            containerStyles={css`
              box-shadow: none;
            `}
          />
        </Accordion.Header>
        <Accordion.Body css={{ backgroundColor: theme.colors.white }}>{renderChild}</Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
};

const getEventBody = (event: CallEvent, callerName?: string) => {
  if ('ivrEvent' in event) {
    return (
      <>
        {event.ivrEvent.dtmfEvents?.map((dtmf) => {
          const dtmfTime = dayjs(dtmf.createdAt).format('hh:mm A');
          return (
            <div key={dtmf.key} css={[styles, trailingDotStyles]}>
              <Dot className='dot' size='large' css={{ backgroundColor: theme.colors.neutral50 }} />
              <Truncated textAlign='left' as={Text} size='medium'>
                <Trans>
                  {callerName} dials <strong>Option {dtmf.key}</strong>
                </Trans>
              </Truncated>
              <Text as='div' size='medium'>
                {dtmfTime}
              </Text>
            </div>
          );
        })}
      </>
    );
  }

  if ('voicemailPromptEvent' in event) {
    const { voicemailPromptEvent, createdAt } = event;
    const createdAtTime = createdAt ? dayjs(createdAt).format('hh:mm A') : '';
    return (
      <>
        <div css={styles}>
          <Dot className='dot' size='large' css={{ backgroundColor: theme.colors.neutral50 }} />
          <Truncated textAlign='left' as={Text} size='medium'>
            {i18next.t('{{name}} left voicemail message', { ns: 'calls', name: callerName })}
          </Truncated>
          <Text as='div' size='medium'>
            {createdAtTime}
          </Text>
        </div>
        <Text
          textAlign='left'
          as='div'
          size='medium'
          css={{ fontStyle: 'italic', paddingLeft: theme.spacing(2.5), paddingTop: theme.spacing(2) }}
        >
          "{i18next.t('{{message}}', { ns: 'calls', message: voicemailPromptEvent.transcription })}"
        </Text>
      </>
    );
  }

  return;
};

const styles = css`
  grid-template-columns: max-content 1fr max-content;
  display: grid;
  gap: ${theme.spacing(1)};
  align-items: center;
`;

const trailingDotStyles = css`
  &:not(:last-child) {
    margin-bottom: ${theme.spacing(2)};
  }

  &:not(:last-child) .dot::after {
    content: '';
    position: absolute;
    top: 115%;
    left: 50%;
    transform: translateX(-50%);
    width: 2px;
    height: 22px;
    background-image: repeating-linear-gradient(
      to bottom,
      ${theme.colors.neutral50},
      ${theme.colors.neutral50} 5px,
      transparent 5px,
      transparent 8px
    );
  }
`;
